import { useEffect, useRef } from "react";
import { useGlobalChatStore } from "../../stores/globalChatStore";
import { ChatMessage } from "./ChatMessage";
import { ChatInput } from "./ChatInput";
import { ChatDateTabs } from "./ChatDateTabs";

// Trash icon (outline style)
function TrashIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

// Chat bubble icon (outline style)
function ChatBubbleIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  );
}

// Sparkles icon for AI suggestions
function SparklesIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>
  );
}

// Loading dots animation
function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="bg-gray-100 rounded-lg px-4 py-2">
        <div className="flex gap-1">
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
        </div>
      </div>
    </div>
  );
}

// Suggested prompts for new conversations
const SUGGESTED_PROMPTS = [
  "How was my training this week?",
  "What should I focus on in my next run?",
  "Am I improving my pace over time?",
  "How is my heart rate zone distribution?",
];

interface SuggestedPromptButtonProps {
  prompt: string;
  onClick: (prompt: string) => void;
  disabled?: boolean;
}

function SuggestedPromptButton({ prompt, onClick, disabled }: SuggestedPromptButtonProps) {
  return (
    <button
      type="button"
      onClick={() => onClick(prompt)}
      disabled={disabled}
      className="text-left px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {prompt}
    </button>
  );
}

export function GlobalChatPanel() {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const {
    messages,
    sessions,
    activeDate,
    isLoading,
    isReadOnly,
    error,
    isInitialized,
    loadHistory,
    loadSessions,
    selectDate,
    sendMessage,
    clearChat,
  } = useGlobalChatStore();

  // Load chat history and sessions on mount
  useEffect(() => {
    if (!isInitialized) {
      loadHistory();
      loadSessions();
    }
  }, [isInitialized, loadHistory, loadSessions]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const handleSend = (content: string) => {
    sendMessage(content);
  };

  const handleClear = () => {
    clearChat();
  };

  const handleSuggestedPrompt = (prompt: string) => {
    sendMessage(prompt);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
        <div className="flex items-center gap-2">
          <SparklesIcon className="w-5 h-5 text-brand-600" />
          <h3 className="font-medium text-gray-900">Training Coach</h3>
        </div>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            disabled={isLoading}
            className="p-1 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
            title="Clear chat"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Date tabs */}
      <ChatDateTabs
        sessions={sessions}
        activeDate={activeDate}
        onSelectDate={selectDate}
      />

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && !isLoading && !isReadOnly && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <ChatBubbleIcon className="w-12 h-12 mb-3 text-gray-300" />
            <p className="text-gray-900 font-medium mb-1">
              Ask about your training
            </p>
            <p className="text-sm text-gray-500 mb-6 max-w-md">
              Get insights about your progress, training patterns, and personalized coaching suggestions.
            </p>

            {/* Suggested prompts */}
            <div className="w-full max-w-md space-y-2">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">
                Try asking
              </p>
              <div className="grid gap-2">
                {SUGGESTED_PROMPTS.map((prompt) => (
                  <SuggestedPromptButton
                    key={prompt}
                    prompt={prompt}
                    onClick={handleSuggestedPrompt}
                    disabled={isLoading}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {messages.length === 0 && !isLoading && isReadOnly && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <ChatBubbleIcon className="w-12 h-12 mb-3 text-gray-300" />
            <p className="text-sm text-gray-500">
              No messages on this day
            </p>
          </div>
        )}

        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}

        {isLoading && <TypingIndicator />}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="p-4 border-t border-gray-200 shrink-0">
        <ChatInput
          onSend={handleSend}
          disabled={isLoading || isReadOnly}
          placeholder={isReadOnly ? "Past conversation (read-only)" : "Ask about your training..."}
        />
      </div>
    </div>
  );
}
